import * as React from 'react';
import { Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CheckoutFormValues,
  TipState,
  tipSchema,
} from '../../types/appointments';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import { isDiscountAllowed, isTipDisplayed } from '../../helpers/payments';
import {
  SelectDiscountPayload,
  selectDiscountSchema,
} from '../../types/discounts';
import useGetDiscounts from '../../hooks/discounts/useGetDiscounts';
import { getDiscountLabel } from '../../helpers/discounts';
import useUser from '../../hooks/global/useUser';
import { getServiceName } from '../../helpers/appointments';
import { Appointment } from '../../interfaces';
import CheckoutItemsSearch from '../checkout/CheckoutItemsSearch';
import useGetCheckoutData from '../../hooks/checkouts/useGetCheckoutData';
import SplitPaymentFlow from '../payments/SplitPaymentFlow';
import { SplitPaymentOnFinishArgs } from '../../types/payments';
import CheckoutFormItems from './CheckoutFormItems';
import CheckoutFormPricingTable from './CheckoutFormPricingTable';

interface Props {
  appointment: Appointment | null;
  values: CheckoutFormValues;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
  setTotalDue: React.Dispatch<React.SetStateAction<number | undefined>>;
  setIsTotalCoveredByGiftCard: React.Dispatch<React.SetStateAction<boolean>>;
  totalDue: number | undefined;
  isSplitPayment: boolean;
  onCloseSplitPayment: () => void;
  onCheckoutSuccess: (args: SplitPaymentOnFinishArgs) => void;
}

export default function PricingTable({
  appointment,
  values,
  setFieldValue,
  setTotalDue,
  setIsTotalCoveredByGiftCard,
  totalDue,
  isSplitPayment,
  onCloseSplitPayment,
  onCheckoutSuccess,
}: Props) {
  const { user } = useUser();

  const { data: discounts = [] } = useGetDiscounts({
    activeOnly: true,
  });

  const customCheckoutItems = React.useMemo(
    () => values.customOrderItems ?? [],
    [values.customOrderItems],
  );

  const discountIsAllowed =
    !!discounts.length &&
    (!values.paymentMethod || isDiscountAllowed(values.paymentMethod));
  const selectedDiscount =
    discountIsAllowed && values.discountId
      ? discounts.find(discount => discount.id === values.discountId)
      : undefined;

  const {
    data: checkoutData,
    isFetching,
    isDebouncing,
    isPlaceholderData,
  } = useGetCheckoutData({
    appointment,
    values,
    discount: selectedDiscount ?? null,
  });
  const isLoading = isFetching || isDebouncing || isPlaceholderData;
  const {
    appointment: apptData,
    order: orderData,
    totalBeforeTaxesAndFeesCents,
    feeCents,
    totalPriceCents,
    hasTaxRate,
    discountAmountCents,
    giftCardAmountCents,
    isTotalCoveredByGiftCard,
  } = checkoutData ?? {};
  const price =
    ((apptData?.originalPriceCents || 0) +
      (orderData?.originalPriceCents || 0)) /
    100;
  const depositAmountPaid = apptData?.depositAmountPaidCents
    ? apptData.depositAmountPaidCents / 100
    : 0;
  const totalBeforeTaxAndFees = (totalBeforeTaxesAndFeesCents || 0) / 100;
  const serviceSalesTax = (apptData?.salesTaxCents || 0) / 100;
  const productSalesTax = (orderData?.salesTaxCents || 0) / 100;
  const customerServiceFee = (feeCents || 0) / 100;
  const tempTotalDue = (totalPriceCents || 0) / 100;
  const discountAmount = (discountAmountCents || 0) / 100;
  const giftCardAmount = (giftCardAmountCents || 0) / 100;
  const showSalesTax = values.paymentMethod !== 'CASH' || values.isCashTaxed;
  const serviceTaxRate = `${
    (user?.business?.businessTaxRates?.serviceTaxRate || 0) * 100
  }%`;
  const productTaxRate = `${
    (user?.business?.businessTaxRates?.productTaxRate || 0) * 100
  }%`;

  const displayTip = isTipDisplayed({
    values,
    appointment,
    user,
  });

  React.useEffect(() => {
    setTotalDue(tempTotalDue);
  }, [tempTotalDue, setTotalDue]);

  React.useEffect(() => {
    setIsTotalCoveredByGiftCard(!!isTotalCoveredByGiftCard);
  }, [isTotalCoveredByGiftCard, setFieldValue, setIsTotalCoveredByGiftCard]);

  const [form, setForm] = React.useState<FormData | null>(null);

  const initialValues = {
    tip: {
      tipAmt: null,
    } as TipState,
    discount: {
      discountId: selectedDiscount?.id || '',
    } as SelectDiscountPayload,
  };

  const tipForm: FormData<TipState> = {
    title: 'Enter tip amount',
    validationSchema: tipSchema,
    initialValues: initialValues.tip,
    inputRows: [
      [
        {
          inputType: 'number',
          label: 'Tip Amount',
          name: 'tipAmt',
        },
      ],
    ],
    onSubmit: async tipVal => {
      setFieldValue('tipAmt', tipVal.tipAmt ? Number(tipVal.tipAmt) : 0);
      setForm(null);
    },
  };

  const discountForm: FormData<SelectDiscountPayload> = {
    title: 'Select discount',
    initialValues: initialValues.discount,
    validationSchema: selectDiscountSchema,
    inputRows: [
      [
        {
          inputType: 'select',
          name: 'discountId',
          options: discounts.map(discount => ({
            value: discount.id,
            label: getDiscountLabel(discount),
          })),
          autoWidth: false,
          placeholder: 'Select discount',
          label: 'Discount',
        },
      ],
    ],
    onSubmit: async newValues => {
      setFieldValue('discountId', newValues.discountId);
      setForm(null);
    },
    submitButtonText: 'Apply',
  };

  const handleRemoveItem = React.useCallback(
    (id: string) => {
      setFieldValue(
        'customOrderItems',
        customCheckoutItems?.filter(item => item.id !== id),
      );
    },
    [customCheckoutItems, setFieldValue],
  );

  const serviceName = appointment ? getServiceName(appointment, user) : '';

  const items = React.useMemo(() => {
    const tempItems: React.ComponentProps<typeof CheckoutFormItems>['items'] =
      [];
    if (appointment) {
      tempItems.push({
        id: appointment.appointmentId,
        disabled: values.paymentMethod === 'FREE',
        name: serviceName,
        price: values.price,
        quantity: 1,
        onPriceChange: (value: number) => setFieldValue('price', value),
      });
    }
    if (customCheckoutItems) {
      tempItems.push(
        ...customCheckoutItems.map(i => ({
          id: i.id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          onPriceChange: (value: number) => {
            setFieldValue(
              'customOrderItems',
              (customCheckoutItems ?? []).map(prevItem =>
                prevItem.id === i.id ? { ...prevItem, price: value } : prevItem,
              ),
            );
          },
          onQuantityChange: (value: number) => {
            // Change the items price to the new value
            setFieldValue(
              'customOrderItems',
              (customCheckoutItems ?? []).map(prevItem =>
                prevItem.id === i.id
                  ? { ...prevItem, quantity: value }
                  : prevItem,
              ),
            );
          },
          onRemove: () => handleRemoveItem(i.id),
        })),
      );
    }
    return tempItems;
  }, [
    appointment,
    customCheckoutItems,
    handleRemoveItem,
    serviceName,
    setFieldValue,
    values.paymentMethod,
    values.price,
  ]);

  return (
    <>
      <Box className="input-group">
        <CheckoutFormItems items={items} />

        {values.paymentMethod !== 'FREE' && (
          <CheckoutItemsSearch appointment={appointment} />
        )}

        <CheckoutFormPricingTable
          preSubtotalItems={[
            {
              label: 'Price',
              value: price + depositAmountPaid,
            },
            {
              label: 'Deposit',
              value: depositAmountPaid,
              isPaid: true,
              subtractFromSubtotal: true,
              visible: !!appointment && !!depositAmountPaid,
            },
            {
              label: 'Tip added',
              value: values.tipAmt,
              visible: !!displayTip && !!values.tipAmt,
              floatingButton: {
                icon: DeleteIcon,
                onClick: () => setFieldValue('tipAmt', 0),
              },
            },
          ]}
          postSubtotalItems={[
            {
              label: selectedDiscount
                ? `Discount - ${getDiscountLabel(selectedDiscount)}`
                : 'Add discount',
              value: -discountAmount,
              cellActionWhenEmpty: {
                label: 'Add discount',
                onClick: () => setForm(discountForm),
              },
              floatingButton: {
                icon: DeleteIcon,
                onClick: () => setFieldValue('discountId', undefined),
              },
            },
            {
              label: 'Add tip',
              value: 0,
              cellActionWhenEmpty: {
                label: 'Add tip',
                onClick: () => setForm(tipForm),
              },
              visible: !!displayTip && !values.tipAmt,
            },
            {
              label: 'Add sales tax',
              value: 0,
              cellActionWhenEmpty: {
                label: 'Add sales tax',
                onClick: () => setFieldValue('isCashTaxed', true),
              },
              visible:
                hasTaxRate &&
                values.paymentMethod === 'CASH' &&
                !values.isCashTaxed,
            },
            {
              label: `Service sales tax (${serviceTaxRate})`,
              value: serviceSalesTax,
              floatingButton:
                values.paymentMethod === 'CASH' && values.isCashTaxed
                  ? {
                      icon: DeleteIcon,
                      onClick: () => setFieldValue('isCashTaxed', false),
                    }
                  : undefined,
              visible: !!serviceSalesTax && showSalesTax,
            },
            {
              label: `Product sales tax (${productTaxRate})`,
              value: productSalesTax,
              floatingButton:
                values.paymentMethod === 'CASH' && values.isCashTaxed
                  ? {
                      icon: DeleteIcon,
                      onClick: () => setFieldValue('isCashTaxed', false),
                    }
                  : undefined,
              visible: !!productSalesTax && showSalesTax,
            },
            {
              label: 'Convenience fee',
              value: customerServiceFee,
              visible: !!customerServiceFee,
            },
          ]}
          subtotal={totalBeforeTaxAndFees}
          total={
            totalDue === 0 && isTotalCoveredByGiftCard && giftCardAmount > 0
              ? giftCardAmount
              : totalDue ?? 0
          }
          isLoading={isLoading}
          minimumPrice={{
            value: appointment?.depositPrice ?? 0,
            errorVisible:
              (!totalDue || totalDue <= 0) &&
              values.paymentMethod !== 'FREE' &&
              !isTotalCoveredByGiftCard,
          }}
        />
        <FormSmallModal
          open={!!form}
          onClose={() => setForm(null)}
          form={form}
        />
      </Box>
      {!!checkoutData && (
        <SplitPaymentFlow
          isOpen={isSplitPayment}
          onFinish={onCheckoutSuccess}
          onClose={onCloseSplitPayment}
          checkoutData={checkoutData}
        />
      )}
    </>
  );
}
